/**
 * API Endpoints — Centralized, typed endpoint definitions.
 * All paths are relative to the API base URL (VITE_API_BASE_URL, which
 * typically ends with /api/v1). The ep() helper ensures paths start with '/'
 * but does NOT add /api/v1 — that prefix lives in the axios baseURL only.
 */

import type {
  User,
  Deck,
  Flashcard,
  StudySession,
  Goal,
  SharedDeck,
  DashboardStats,
  StudyTrend,
  CategoryBreakdown,
  HeatmapDay,
  AppNotification,
  ImportResult,
  SRReviewResult,
  PaginatedResponse,
  PaginationParams,
  DeckSource,
  CreateSourcePayload,
  UpdateSourcePayload,
  ReorderSourcesPayload,
  SourceWithHeadings,
  DeckListQueryParams,
  Lesson,
  LessonWithBlocks,
  LessonBlock,
  CreateLessonPayload,
  UpdateLessonPayload,
  CreateLessonBlockPayload,
  UpdateLessonBlockPayload,
  ReorderBlocksPayload,
  ReorderLessonsPayload,
  TestHistoryItem,
  ContentVersion,
  PublishVersionPayload,
  UpdateGovernancePayload,
} from './types';
import type {
  CreateDeckPayload,
  UpdateDeckPayload,
  CreateFlashcardPayload,
  UpdateFlashcardPayload,
  StartStudySessionPayload,
  ReviewCardPayload,
  CreateGoalPayload,
  UpdateGoalPayload,
  ShareDeckPayload,
  ImportFlashcardsPayload,
  ExportFlashcardsPayload,
  UpdateUserPayload,
  AdminUser,
  AdminUsersListResponse,
  AdminUserQueryParams,
  SendNotificationPayload,
  AdminOrgSummary,
  AdminNotificationTargetType,
} from './types';

/**
 * Build a relative endpoint path.
 * The /api/v1 prefix is already in the axios instance baseURL
 * (VITE_API_BASE_URL in client.ts), so we only ensure the path
 * starts with '/'.  This prevents the double-prefix bug
 * /api/v1/api/v1/... that occurs when ep() also prepends /api/v1.
 */
function ep(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

// ============================================================
// Query string builders
// ============================================================

function buildQuery(params?: PaginationParams): string {
  if (!params) return '';
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortOrder) query.set('sortOrder', params.sortOrder);
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

// ============================================================
// Types for query parameters
// ============================================================

export type DeckQueryParams = PaginationParams & {
  search?: string;
  category?: string;
  tag?: string;
};

export type FlashcardQueryParams = PaginationParams & {
  type?: string;
  tag?: string;
  search?: string;
};

function buildDeckQuery(params?: DeckQueryParams): string {
  if (!params) return '';
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortOrder) query.set('sortOrder', params.sortOrder);
  if (params.search) query.set('search', params.search);
  if (params.category) query.set('category', params.category);
  if (params.tag) query.set('tag', params.tag);
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

function buildFlashcardQuery(params?: FlashcardQueryParams): string {
  if (!params) return '';
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortOrder) query.set('sortOrder', params.sortOrder);
  if (params.type) query.set('type', params.type);
  if (params.tag) query.set('tag', params.tag);
  if (params.search) query.set('search', params.search);
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

// ============================================================
// Auth endpoints
// ============================================================

export const authEndpoints = {
  login: ep('/auth/login'),
  callback: ep('/auth/callback'),
  refresh: ep('/auth/refresh'),
  logout: ep('/auth/logout'),
  me: ep('/auth/me'),
  // MFA endpoints (issue_174)
  mfaStatus: ep('/auth/mfa-status'),
  mfaSetup: ep('/auth/mfa/setup'),
  mfaVerifySetup: ep('/auth/mfa/verify-setup'),
  mfaDisable: ep('/auth/mfa'),
  mfaVerifyLogin: ep('/auth/mfa/verify-login'),
  // MFA cancel — cancels a pending MFA login session in Redis (issue_176)
  mfaCancel: ep('/auth/mfa/cancel'),
  mfaForceAll: ep('/auth/mfa/force-all'),
} as const;

// ============================================================
// User endpoints
// ============================================================

export const userEndpoints = {
  profile: ep('/users/me'),
  updateProfile: ep('/users/me'),
  statistics: ep('/users/me/statistics'),
  deleteAccount: ep('/users/me'),
  // issue_163: onboarding endpoint — POST /users/me/become-creator
  becomeCreator: ep('/users/me/become-creator'),
} as const;

// ============================================================
// Deck endpoints
// ============================================================

export const deckEndpoints = {
  list: (params?: DeckQueryParams) => ep(`/decks${buildDeckQuery(params)}`),
  getById: (id: string) => ep(`/decks/${id}`),
  create: ep('/decks'),
  update: (id: string) => ep(`/decks/${id}`),
  delete: (id: string) => ep(`/decks/${id}`),
  shared: (params?: PaginationParams) => ep(`/decks/shared${buildQuery(params)}`),
  public: (params?: PaginationParams) => ep(`/decks/public${buildQuery(params)}`),
  share: (id: string) => ep(`/decks/${id}/share`),
  // Deck Sources
  sources: (deckId: string) => ep(`/decks/${deckId}/sources`),
  source: (deckId: string, sourceId: string) => ep(`/decks/${deckId}/sources/${sourceId}`),
  reorderSources: (deckId: string) => ep(`/decks/${deckId}/sources/reorder`),
  sourceHeadings: (deckId: string) => ep(`/decks/${deckId}/sources/headings`),
  // Test di Certificazione
  enableTest: (deckId: string) => ep(`/decks/${deckId}/test/enable`),
  disableTest: (deckId: string) => ep(`/decks/${deckId}/test/disable`),
  testHistory: (deckId: string, limit?: number) =>
      ep(`/decks/${deckId}/test/history${limit ? `?limit=${limit}` : ''}`),
  // Content Versioning
  publishVersion: (deckId: string) => ep(`/decks/${deckId}/publish-version`),
  versions: (deckId: string) => ep(`/decks/${deckId}/versions`),
  governance: (deckId: string) => ep(`/decks/${deckId}/governance`),
} as const;

// ============================================================
// Deck Source endpoints (standalone — used by useSources hooks)
// ============================================================

export const sourceEndpoints = {
  sources: (deckId: string) => ep(`/decks/${deckId}/sources`),
  source: (deckId: string, sourceId: string) => ep(`/decks/${deckId}/sources/${sourceId}`),
  reorderSources: (deckId: string) => ep(`/decks/${deckId}/sources/reorder`),
  uploadSource: (deckId: string) => ep(`/decks/${deckId}/sources/upload`),
  sourceHeadings: (deckId: string) => ep(`/decks/${deckId}/sources/headings`),
} as const;

// ============================================================
// Lesson endpoints (standalone — used by useLessons hooks)
// ============================================================

export const lessonEndpoints = {
  // Lesson CRUD
  list: (deckId: string) => ep(`/decks/${deckId}/lessons`),
  getById: (deckId: string, lessonId: string) => ep(`/decks/${deckId}/lessons/${lessonId}`),
  create: (deckId: string) => ep(`/decks/${deckId}/lessons`),
  update: (deckId: string, lessonId: string) => ep(`/decks/${deckId}/lessons/${lessonId}`),
  delete: (deckId: string, lessonId: string) => ep(`/decks/${deckId}/lessons/${lessonId}`),
  reorderLessons: (deckId: string) => ep(`/decks/${deckId}/lessons/reorder`),
  // Block CRUD
  addBlock: (deckId: string, lessonId: string) => ep(`/decks/${deckId}/lessons/${lessonId}/blocks`),
  updateBlock: (deckId: string, lessonId: string, blockId: string) =>
    ep(`/decks/${deckId}/lessons/${lessonId}/blocks/${blockId}`),
  deleteBlock: (deckId: string, lessonId: string, blockId: string) =>
    ep(`/decks/${deckId}/lessons/${lessonId}/blocks/${blockId}`),
  reorderBlocks: (deckId: string, lessonId: string) =>
    ep(`/decks/${deckId}/lessons/${lessonId}/blocks/reorder`),
  // Marp file upload
  uploadMarp: (deckId: string, lessonId: string) =>
    ep(`/decks/${deckId}/lessons/${lessonId}/blocks/upload`),
  // Content Versioning
  publishVersion: (deckId: string, lessonId: string) =>
    ep(`/decks/${deckId}/lessons/${lessonId}/publish-version`),
  versions: (deckId: string, lessonId: string) =>
    ep(`/decks/${deckId}/lessons/${lessonId}/versions`),
  governance: (deckId: string, lessonId: string) =>
    ep(`/decks/${deckId}/lessons/${lessonId}/governance`),
} as const;

// ============================================================
// Deck List (Collection) Endpoints
// ============================================================

function buildDeckListQuery(params?: DeckListQueryParams): string {
  if (!params) return '';
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  if (params.isPublic !== undefined) query.set('isPublic', String(params.isPublic));
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortOrder) query.set('sortOrder', params.sortOrder);
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

export const deckListEndpoints = {
  list: (params?: DeckListQueryParams) => ep(`/deck-lists${buildDeckListQuery(params)}`),
  public: (params?: PaginationParams) => ep(`/deck-lists/public${buildQuery(params)}`),
  getById: (id: string) => ep(`/deck-lists/${id}`),
  create: ep('/deck-lists'),
  update: (id: string) => ep(`/deck-lists/${id}`),
  delete: (id: string) => ep(`/deck-lists/${id}`),
  addDeck: (listId: string) => ep(`/deck-lists/${listId}/decks`),
  removeDeck: (listId: string, deckId: string) => ep(`/deck-lists/${listId}/decks/${deckId}`),
  reorderDecks: (listId: string) => ep(`/deck-lists/${listId}/decks/reorder`),
  copy: (id: string) => ep(`/deck-lists/${id}/copy`),
} as const;

// ============================================================
// Flashcard endpoints
// ============================================================

export const flashcardEndpoints = {
  getById: (id: string) => ep(`/flashcards/${id}`),
  update: (id: string) => ep(`/flashcards/${id}`),
  delete: (id: string) => ep(`/flashcards/${id}`),
  listByDeck: (deckId: string, params?: FlashcardQueryParams) =>
    ep(`/decks/${deckId}/flashcards${buildFlashcardQuery(params)}`),
  createInDeck: (deckId: string) => ep(`/decks/${deckId}/flashcards`),
  bulkCreate: (deckId: string) => ep(`/decks/${deckId}/flashcards/bulk`),
  search: (deckId: string, query: string, params?: PaginationParams) => {
    const qs = new URLSearchParams();
    qs.set('q', query);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.sortBy) qs.set('sortBy', params.sortBy);
    if (params?.sortOrder) qs.set('sortOrder', params.sortOrder);
    return ep(`/decks/${deckId}/flashcards/search?${qs.toString()}`);
  },
  duplicate: (id: string) => ep(`/flashcards/${id}/duplicate`),
} as const;

// ============================================================
// Export endpoints
// ============================================================

export const exportEndpoints = {
  exportDeck: (deckId: string) => ep(`/export/decks/${deckId}`),
} as const;

// ============================================================
// Import endpoints
// ============================================================

export const importEndpoints = {
  /** GET /import/formats — list supported import/export formats */
  formats: ep('/import/formats'),
  /** POST /import/upload — upload file (multipart), auto-detect format, parse, return preview */
  upload: ep('/import/upload'),
  /** POST /import/confirm — confirm import after preview, select target deck */
  confirm: ep('/import/confirm'),
} as const;

// ============================================================
// Import/Export endpoints (legacy compat — prefer importEndpoints / exportEndpoints)
// ============================================================

export const importExportEndpoints = {
  import: importEndpoints.upload,
  export: (deckId: string) =>
    ep(`/export/decks/${deckId}`),
} as const;

// ============================================================
// Tag endpoints
// ============================================================

export const tagEndpoints = {
  list: ep('/tags'),
  decksByTag: (tag: string) => ep(`/tags/${encodeURIComponent(tag)}/decks`),
} as const;

// ============================================================
// Study session endpoints
// ============================================================

export const studyEndpoints = {
  // Session management
  // startSession accepts any SessionType in the body, including SessionType.TEST.
  // The payload type StartStudySessionPayload uses `type: SessionType` which
  // already includes the 'test' value from the shared enum (issue_96 point 16.4).
  startSession: ep('/sr/session/start'),
  endSession: (id: string) => ep(`/sr/session/${id}/end`),
  getSessions: (params?: PaginationParams) => ep(`/sr/sessions${buildQuery(params)}`),
  getSession: (id: string) => ep(`/sr/sessions/${id}`),
  getSessionQueue: (id: string) => ep(`/sr/sessions/${id}/queue`),
  getSessionStats: (id: string) => ep(`/sr/sessions/${id}/stats`),
  getSessionReviews: (id: string) => ep(`/sr/sessions/${id}/reviews`),

  // Spaced repetition
  getQueue: ep('/sr/queue'),
  getHistory: (flashcardId: string) => ep(`/sr/history/${flashcardId}`),
  getGoalPriorityCards: (goalId: string) => ep(`/sr/goals/${goalId}/priority-cards`),
} as const;

// ============================================================
// Spaced repetition endpoints
// ============================================================

export const srEndpoints = {
  getCardState: (cardId: string) => ep(`/sr/cards/${cardId}/state`),
  getDueCards: (deckId?: string) => ep(`/sr/due${deckId ? `?deckId=${deckId}` : ''}`),
  history: (flashcardId: string) => ep(`/sr/history/${flashcardId}`),
  sessionEnd: (id: string) => ep(`/sr/session/${id}/end`),
  sessions: (params?: PaginationParams) => ep(`/sr/sessions${buildQuery(params)}`),
  sessionStats: (id: string) => ep(`/sr/sessions/${id}/stats`),
  goalPriorityCards: (goalId: string) => ep(`/sr/goals/${goalId}/priority-cards`),
} as const;

// ============================================================
// Goal endpoints
// ============================================================

export const goalEndpoints = {
  list: (params?: PaginationParams) => ep(`/goals${buildQuery(params)}`),
  getById: (id: string) => ep(`/goals/${id}`),
  create: ep('/goals'),
  update: (id: string) => ep(`/goals/${id}`),
  delete: (id: string) => ep(`/goals/${id}`),
} as const;

// ============================================================
// Dashboard endpoints
// ============================================================

export const dashboardEndpoints = {
  stats: ep('/dashboard/overview'),
  trends: (days?: number) => ep(`/dashboard/trends${days ? `?days=${days}` : ''}`),
  categoryBreakdown: ep('/dashboard/category-breakdown'),
  heatmap: (year?: number) => ep(`/dashboard/heatmap${year ? `?year=${year}` : ''}`),
} as const;

// ============================================================
// Sharing endpoints
// ============================================================

export const sharingEndpoints = {
  shareDeck: ep('/sharing/decks'),
  getSharedDecks: (params?: PaginationParams) => ep(`/sharing/decks${buildQuery(params)}`),
  getSharedDeck: (id: string) => ep(`/sharing/decks/${id}`),
  revokeShare: (id: string) => ep(`/sharing/decks/${id}`),
  acceptShare: (token: string) => ep(`/sharing/accept/${token}`),
} as const;

// ============================================================
// Notification endpoints
// ============================================================

export const notificationEndpoints = {
  list: (params?: PaginationParams) => ep(`/notifications${buildQuery(params)}`),
  markAsRead: (id: string) => ep(`/notifications/${id}/read`),
  markAllAsRead: ep('/notifications/read-all'),
  delete: (id: string) => ep(`/notifications/${id}`),
} as const;

// ============================================================
// Admin endpoints (issue_216)
// ============================================================

function buildAdminUserQuery(params?: AdminUserQueryParams): string {
  if (!params) return '';
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  if (params.role) query.set('role', params.role);
  if (params.organizationId) query.set('organizationId', params.organizationId);
  if (params.isActive !== undefined) query.set('isActive', String(params.isActive));
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortOrder) query.set('sortOrder', params.sortOrder);
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

export const adminEndpoints = {
  // User management
  users: (params?: AdminUserQueryParams) => ep(`/admin/users${buildAdminUserQuery(params)}`),
  userById: (userId: string) => ep(`/admin/users/${userId}`),
  disableUser: (userId: string) => ep(`/admin/users/${userId}/disable`),
  enableUser: (userId: string) => ep(`/admin/users/${userId}/enable`),
  // Organization management
  organizations: ep('/admin/organizations'),
  disableOrgMembers: (orgId: string) => ep(`/admin/organizations/${orgId}/disable-all`),
  enableOrgMembers: (orgId: string) => ep(`/admin/organizations/${orgId}/enable-all`),
  // Notification sending
  sendNotification: ep('/admin/notifications/send'),
} as const;

// ============================================================
// Type helpers for endpoint responses
// ============================================================

export type AuthApi = {
  login: { response: { accessToken: string; refreshToken: string } };
  callback: { params: { code: string; state: string }; response: { accessToken: string; refreshToken: string } };
  refresh: { body: { refreshToken: string }; response: { accessToken: string; refreshToken: string } };
  logout: { body: { refreshToken: string }; response: void };
  me: { response: User };
};

export type DeckApi = {
  list: { params?: DeckQueryParams; response: PaginatedResponse<Deck> };
  getById: { params: { id: string }; response: Deck };
  create: { body: CreateDeckPayload; response: Deck };
  update: { params: { id: string }; body: UpdateDeckPayload; response: Deck };
  delete: { params: { id: string }; response: void };
  shared: { params?: PaginationParams; response: PaginatedResponse<Deck> };
  public: { params?: PaginationParams; response: PaginatedResponse<Deck> };
  share: { params: { id: string }; body: ShareDeckPayload; response: SharedDeck };
  sources: { params: { deckId: string }; response: DeckSource[] };
  source: { params: { deckId: string; sourceId: string }; response: DeckSource };
  createSource: { params: { deckId: string }; body: CreateSourcePayload; response: DeckSource };
  updateSource: { params: { deckId: string; sourceId: string }; body: UpdateSourcePayload; response: DeckSource };
  deleteSource: { params: { deckId: string; sourceId: string }; response: void };
  reorderSources: { params: { deckId: string }; body: ReorderSourcesPayload; response: DeckSource[] };
  sourceHeadings: { params: { deckId: string }; response: SourceWithHeadings[] };
  // Test di Certificazione
  enableTest: { params: { deckId: string }; response: Deck };
  disableTest: { params: { deckId: string }; response: Deck };
  testHistory: { params: { deckId: string }; response: TestHistoryItem[] };
  // Content Versioning
  publishVersion: { params: { deckId: string }; body: PublishVersionPayload; response: ContentVersion };
  versions: { params: { deckId: string }; response: ContentVersion[] };
  governance: { params: { deckId: string }; body: UpdateGovernancePayload; response: Deck };
};

export type LessonApi = {
  list: { params: { deckId: string }; response: Lesson[] };
  getById: { params: { deckId: string; lessonId: string }; response: LessonWithBlocks };
  create: { params: { deckId: string }; body: CreateLessonPayload; response: Lesson };
  update: { params: { deckId: string; lessonId: string }; body: UpdateLessonPayload; response: Lesson };
  delete: { params: { deckId: string; lessonId: string }; response: void };
  reorderLessons: { params: { deckId: string }; body: ReorderLessonsPayload; response: Lesson[] };
  addBlock: { params: { deckId: string; lessonId: string }; body: CreateLessonBlockPayload; response: LessonBlock };
  updateBlock: { params: { deckId: string; lessonId: string; blockId: string }; body: UpdateLessonBlockPayload; response: LessonBlock };
  deleteBlock: { params: { deckId: string; lessonId: string; blockId: string }; response: void };
  reorderBlocks: { params: { deckId: string; lessonId: string }; body: ReorderBlocksPayload; response: LessonBlock };
  // Content Versioning
  publishVersion: { params: { deckId: string; lessonId: string }; body: PublishVersionPayload; response: ContentVersion };
  versions: { params: { deckId: string; lessonId: string }; response: ContentVersion[] };
  governance: { params: { deckId: string; lessonId: string }; body: UpdateGovernancePayload; response: Lesson };
};

export type FlashcardApi = {
  getById: { params: { id: string }; response: Flashcard };
  update: { params: { id: string }; body: UpdateFlashcardPayload; response: Flashcard };
  delete: { params: { id: string }; response: void };
  listByDeck: { params: { deckId: string; query?: FlashcardQueryParams }; response: PaginatedResponse<Flashcard> };
  createInDeck: { params: { deckId: string }; body: CreateFlashcardPayload; response: Flashcard };
  bulkCreate: { params: { deckId: string }; body: CreateFlashcardPayload[]; response: Flashcard[] };
  search: { params: { deckId: string; query: string }; response: PaginatedResponse<Flashcard> };
  duplicate: { params: { id: string }; response: Flashcard };
};

export type StudyApi = {
  startSession: { body: StartStudySessionPayload; response: StudySession };
  getSession: { params: { id: string }; response: StudySession };
  endSession: { params: { id: string }; response: StudySession };
  reviewCard: { params: { sessionId: string }; body: ReviewCardPayload; response: SRReviewResult };
};

export type GoalApi = {
  list: { params?: PaginationParams; response: PaginatedResponse<Goal> };
  getById: { params: { id: string }; response: Goal };
  create: { body: CreateGoalPayload; response: Goal };
  update: { params: { id: string }; body: UpdateGoalPayload; response: Goal };
  delete: { params: { id: string }; response: void };
};

export type DashboardApi = {
  stats: { response: DashboardStats };
  trends: { params?: { days: number }; response: StudyTrend[] };
  categoryBreakdown: { response: CategoryBreakdown[] };
  heatmap: { params?: { year: number }; response: HeatmapDay[] };
};

export type SharingApi = {
  shareDeck: { body: ShareDeckPayload; response: SharedDeck };
  getSharedDecks: { params?: PaginationParams; response: PaginatedResponse<SharedDeck> };
  revokeShare: { params: { id: string }; response: void };
  acceptShare: { params: { token: string }; response: SharedDeck };
};

export type ImportExportApi = {
  import: { body: ImportFlashcardsPayload; response: ImportResult };
  export: { params: { deckId: string; format: string }; response: Blob };
};

export type NotificationApi = {
  list: { params?: PaginationParams; response: PaginatedResponse<AppNotification> };
  markAsRead: { params: { id: string }; response: void };
  markAllAsRead: { response: void };
  delete: { params: { id: string }; response: void };
};

export type UserApi = {
  profile: { response: User };
  updateProfile: { body: UpdateUserPayload; response: User };
  deleteAccount: { response: void };
  // issue_163: onboarding endpoint — POST /users/me/become-creator
  becomeCreator: { response: User };
};

export type AdminApi = {
  users: { params?: AdminUserQueryParams; response: AdminUsersListResponse };
  userById: { params: { userId: string }; response: AdminUser };
  disableUser: { params: { userId: string }; response: { message: string } };
  enableUser: { params: { userId: string }; response: { message: string } };
  organizations: { response: AdminOrgSummary[] };
  disableOrgMembers: { params: { orgId: string }; response: { disabledCount: number } };
  enableOrgMembers: { params: { orgId: string }; response: { enabledCount: number } };
  sendNotification: { body: SendNotificationPayload; response: { recipientCount: number } };
};